import React, { createContext, useContext, useEffect, useState } from "react";
import { useHttp } from "../Common/HttpProvider.jsx";
import { useLocale } from "../i18n/I18nProvider.jsx";
import { useBaseData } from "./BaseDataProvider.jsx";

/**
 * @typedef {Object} MissionsProviderContext
 * @property {Mission[]} missions
 * @property {Number} coinBalance
 * @property {Array} missionRewards
 * @property {Function} setMissionRewards
 * @property {FnAsyncNumberVoid} startMission
 */

/** @type {React.Context<MissionsProviderContext>} */
const missionsContext = createContext({
  missions: [],
  missionRewards: [],
  setMissionRewards: () => {},
  /** @type {FnAsyncNumberVoid} */ startMission: () => {},
});

/** @returns {MissionsProviderContext} */
export const useMissions = () => useContext(missionsContext);

/**
 * @param {React.ReactNode} children
 * @returns {React.ReactNode}
 * @constructor
 */
const MissionsProvider = ({ children }) => {
  const { get, patch } = useHttp();
  const { locale } = useLocale();
  const { subject, coinBalance, setCoinBalance } = useBaseData();
  const [missions, setMissions] = useState(/** @type {Mission[]} */ []);
  const [missionRewards, setMissionRewards] = useState([]);

  const subjectId = subject?.subjectId;

  /** */
  const requestData = async () => {
    const resp = await get("missionsTree");
    if (resp) {
      setMissions(resp);
    }
  };

  // load news at start
  useEffect(() => {
    requestData().then();
    //eslint-disable-next-line react-hooks/exhaustive-deps
  }, [locale, subjectId]);

  /** @type {FnAsyncNumberVoid} */
  const startMission = async (id) => {
    const resp = await patch(`missions/start/${id}`, {});
    if (resp?.wallet?.coinBalance !== coinBalance) {
      setCoinBalance(resp.wallet.coinBalance);
    }
    if (resp?.rewards?.large?.items.length > 0) {
      setMissionRewards(resp.rewards.large?.items);
    }
    if (resp.missionOneTree) {
      setMissions((prev) =>
        prev.map((mission) => (mission.id === resp.missionOneTree.id ? resp.missionOneTree : mission)),
      );
    }
  };

  const value = {
    missions,
    missionRewards,
    setMissionRewards,
    startMission,
  };

  return <missionsContext.Provider value={value}>{children}</missionsContext.Provider>;
};

export default MissionsProvider;
